import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Save, Eye, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

export const OverviewSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    page_password: 'CultureRises',
    page_title: 'Cultural Revolution',
    page_subtitle: 'Private Overview',
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/overview-settings`);
        if (response.status === 403) {
          navigate('/cms');
          return;
        }
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/overview-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        alert('Settings saved successfully!');
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Overview Page Settings
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a 
              href="/overview" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            >
              <Eye size={16} />
              Preview
            </a>
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to CMS
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl editorial-serif mb-2">Private Overview Page</h1>
            <p className="text-muted-foreground">
              Configure the password-protected overview page at <code className="text-sm bg-muted px-1 rounded">/overview</code>
            </p>
          </div>

          <Card className="p-6 space-y-6">
            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-1">Page Password</label>
              <input
                type="text"
                value={settings.page_password || ''}
                onChange={(e) => setSettings({ ...settings, page_password: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Enter password for page access"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Users must enter this password to view the overview page
              </p>
            </div>

            {/* Page Title */}
            <div>
              <label className="block text-sm font-medium mb-1">Page Title</label>
              <input
                type="text"
                value={settings.page_title || ''}
                onChange={(e) => setSettings({ ...settings, page_title: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Cultural Revolution"
              />
            </div>

            {/* Page Subtitle */}
            <div>
              <label className="block text-sm font-medium mb-1">Page Subtitle</label>
              <input
                type="text"
                value={settings.page_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, page_subtitle: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border rounded"
                placeholder="Private Overview"
              />
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded">
              <p className="text-sm text-blue-400">
                <strong>Note:</strong> The overview page content (sections, text, images) is currently 
                hardcoded in the frontend code. To edit the full content structure, you'll need to 
                modify the <code className="bg-blue-500/20 px-1 rounded">PrivateOverviewPage.jsx</code> file directly.
              </p>
              <p className="text-sm text-blue-400 mt-2">
                This settings page allows you to change the password and basic page metadata.
              </p>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="p-6">
            <h3 className="font-medium mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <a 
                href="/overview" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full text-left px-4 py-3 bg-muted/30 hover:bg-muted rounded transition-colors"
              >
                <div className="font-medium">View Overview Page</div>
                <div className="text-sm text-muted-foreground">Open the private overview page in a new tab</div>
              </a>
              <div className="px-4 py-3 bg-muted/30 rounded">
                <div className="font-medium">Share with Partners</div>
                <div className="text-sm text-muted-foreground">
                  Share URL: <code className="bg-muted px-1 rounded">culturalrevolution.io/overview</code>
                </div>
                <div className="text-sm text-muted-foreground">
                  Password: <code className="bg-muted px-1 rounded">{settings.page_password}</code>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OverviewSettingsPage;
